import {
  type BuilderViewBase,
  type BuilderViewKey,
  type BuilderViewSourceSchema,
  type BuilderViewSubmitRule
} from '@/types/schema/BuilderView';
import { type DateTimeFieldRangeType } from '@/types/schema/fields';
import { type KnackFieldKey } from '@/types/schema/KnackField';
import { type BaseFieldOperator } from '@/types/schema/operators/field-operators';
import { type FormViewAddressInput } from '@/types/schema/views/form/Address';
import { type FormViewBooleanInput } from '@/types/schema/views/form/Boolean';
import { type FormViewConnectionInput } from '@/types/schema/views/form/Connection';
import { type FormViewCurrencyInput } from '@/types/schema/views/form/Currency';
import { type FormViewDateTimeInput } from '@/types/schema/views/form/DateTime';
import { type FormViewEmailInput } from '@/types/schema/views/form/Email';
import { type FormViewEquationInput } from '@/types/schema/views/form/Equation';
import { type FormViewFileInput } from '@/types/schema/views/form/File';
import { type FormViewImageInput } from '@/types/schema/views/form/Image';
import { type FormViewLinkInput } from '@/types/schema/views/form/Link';
import { type FormViewMultipleChoiceInput } from '@/types/schema/views/form/MultipleChoice';
import { type FormViewNameInput } from '@/types/schema/views/form/Name';
import { type FormViewNumberInput } from '@/types/schema/views/form/Number';
import { type FormViewParagraphTextInput } from '@/types/schema/views/form/ParagraphText';
import { type FormViewPasswordInput } from '@/types/schema/views/form/Password';
import { type FormViewPhoneInput } from '@/types/schema/views/form/Phone';
import { type FormViewRatingInput } from '@/types/schema/views/form/Rating';
import { type FormViewRichTextInput } from '@/types/schema/views/form/RichText';
import { type FormViewShortTextInput } from '@/types/schema/views/form/ShortText';
import { type FormViewSignatureInput } from '@/types/schema/views/form/Signature';
import { type FormViewTimerInput } from '@/types/schema/views/form/Timer';
import { type FormViewUserRolesInput } from '@/types/schema/views/form/UserRoles';

export interface FormView extends BuilderViewBase {
  type: 'form' | 'registration';
  action: 'create' | 'update' | 'insert';
  columns: unknown[];
  links: unknown[];
  inputs: unknown[];
  source: BuilderViewSourceSchema;
  groups: FormViewGroup[];
  submitButtonText: string;
  rules: {
    submits: BuilderViewSubmitRule[];
    fields?: FormViewDisplayRule[];
    records?: unknown[];
    emails?: Array<{
      action: string;
      connection: null;
      criteria: unknown[];
      values: Array<{
        type: string;
        input: string;
        connection_field: string;
        value: string;
      }>;
      email: {
        from_name: string;
        from_email: string;
        subject: string;
        message: string;
        recipients: Array<{
          recipient_mode: string;
          recipient_type: string;
          email: string;
          field: string;
        }>;
      };
      key: string;
    }>;
  };
  alert: string;
  design: unknown;
  parent?: BuilderViewKey;
}

export type FormViewGroup = {
  id: string;
  columns: FormViewGroupColumn[];
};

export type FormViewGroupColumn = {
  id: string;
  inputs: FormViewInput[];
};

export const FORM_VIEW_LAYOUT_COLUMNS = ['1', '2', '3'] as const;
export type FormViewLayoutColumns = (typeof FORM_VIEW_LAYOUT_COLUMNS)[number];

export interface FormViewDisplayRule {
  key: string;
  criteria: FormViewDisplayRuleCriteria[];
  actions: FormViewDisplayRuleAction[];
}

export interface FormViewDisplayRuleCriteria {
  field: KnackFieldKey;
  operator: BaseFieldOperator;
  value:
    | string

    // Only for boolean fields
    | boolean

    // If the value is an array of strings, it means the field is a connection. The array will contain the id of the record (e.g. ["66b3d6f8f160620293d83294"])
    | string[]

    // Value for date/time fields
    | {
        date: string;
        all_day?: boolean;
        hours?: number;
        minutes?: number;
        time?: string;
        am_pm?: string;
      };

  // These properties are specific to date/time fields
  range?: string | number;
  type?: DateTimeFieldRangeType;
}

export const FORM_VIEW_DISPLAY_RULE_ACTION_TYPES = [
  'show',
  'hide',
  'label',
  'show-hide',
  'hide-show'
] as const;
export type FormViewDisplayRuleActionType = (typeof FORM_VIEW_DISPLAY_RULE_ACTION_TYPES)[number];

export interface FormViewDisplayRuleAction {
  field: KnackFieldKey;
  action: FormViewDisplayRuleActionType;
  value: string;
}

export interface FormViewFieldInputBase {
  id: KnackFieldKey;
  field: {
    key: KnackFieldKey;
  };
  instructions: string;
  label: string;
  value?: string;
  read_only?: boolean; // This only comes when editing a record and enabling the read-only option on the input
}

export interface FormViewSectionBreakInput {
  id: string;
  type: 'section_break';
  copy: string;
  label: string;
  instructions: string;
}

export interface FormViewDividerInput {
  id: string;
  type: 'divider';
  instructions: string;
}

export type FormViewStaticInput = FormViewSectionBreakInput | FormViewDividerInput;

export type FormViewFieldInput =
  | FormViewShortTextInput
  | FormViewParagraphTextInput
  | FormViewRichTextInput
  | FormViewNumberInput
  | FormViewCurrencyInput
  | FormViewEquationInput
  | FormViewNameInput
  | FormViewAddressInput
  | FormViewEmailInput
  | FormViewMultipleChoiceInput
  | FormViewBooleanInput
  | FormViewFileInput
  | FormViewImageInput
  | FormViewDateTimeInput
  | FormViewTimerInput
  | FormViewPhoneInput
  | FormViewLinkInput
  | FormViewSignatureInput
  | FormViewRatingInput
  | FormViewConnectionInput
  | FormViewPasswordInput
  | FormViewUserRolesInput;

export type FormViewInput = FormViewFieldInput | FormViewStaticInput;
